// AssetDetails.js - Production Asset Details page.
// Promoted from the PRD-43 V3 prototype on 2026-04-11 after all 8 data-mapping
// gaps were fixed. The previous implementation is preserved at
// AssetDetails_legacy.js for rollback.
//
// Layout:
//   - Compact 6-card metrics row (Scan Score · Vulns · Ports · Subdomains ·
//     Technologies · Drift) — vuln/ports/subdomains/tech cards click to the
//     matching Discovery/Findings sub-tab
//   - Visualization row: risk trend line (severity-weighted score derived
//     from per-scan vuln counts) + top-3 open drift events sorted by severity
//   - Attack Surface Analysis (collapsible, default open)
//   - Executive Summary (collapsible, default open)
//   - Side-by-side Remediation Priorities + Hardening Recommendations
//     abbreviated cards (click any row deep-links to full Findings tab)
//   - 2-level tab system: Discovery / Findings / Intelligence / History
//   - Discovery sub-tabs reuse AssetDataSections (filtered to one section per
//     tab via visibleSections + autoExpand + hideHeaders) so all original
//     subtleties are preserved: suppression, vuln consolidation, tech history,
//     CSV export, cloud enumeration, per-subdomain tech breakdown.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    getAsset,
    getAssetHistory,
    triggerAssetScan,
    getAssetRawDataUrl,
    downloadAssetFile,
    getAssetScoreBreakdown,
    getRiskTimeline,
    getAssetScoreExplain
} from '../services/assetService';
import { getEvents } from '../services/driftService';
import { getAssetSuppressions } from '../services/suppressionService';
import { getCachedTierInfo } from '../services/tierService';
import CleanModernLayout from '../components/layouts/CleanModernLayout';
import MetricsCard from '../components/prototypes/MetricsCard';
import CollapsibleSection from '../components/prototypes/CollapsibleSection';
import QuickActions from '../components/prototypes/QuickActions';
import RiskTrendChart from '../components/prototypes/RiskTrendChart';
import ScorePenaltyBar from '../components/prototypes/ScorePenaltyBar';
import ScoreAttributionPanel from '../components/prototypes/ScoreAttributionPanel';
import Tooltip from '../components/common/Tooltip';
import DriftEventsStrip from '../components/prototypes/DriftEventsStrip';
import GuidanceCards from '../components/prototypes/GuidanceCards';
import AssetDataSections from '../components/Assets/AssetDataSections';
import FindingsTab from '../components/Assets/FindingsTab';
import DriftTimeline from '../components/DriftTimeline';
import DriftEventsList from '../components/DriftEventsList';
import DriftFindingsTrend from '../components/DriftFindingsTrend';
import ScanDetailsTab from '../components/Assets/ScanDetailsTab';
import DeepSecurityTab from '../components/Assets/DeepSecurityTab';
import WafBlockingBanner, { wafVendorLabel } from '../components/WafBlockingBanner';
import CoverageBanner from '../components/CoverageBanner';
import './AssetDetails_v3.css';

// ---------- Helpers ----------

function fmtDate(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    } catch { return iso; }
}

function fmtDateShort(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return iso; }
}

// PRD-51: Risk score severity — higher = riskier
function riskScoreSeverity(score) {
    if (score == null || isNaN(score)) return { label: 'UNKNOWN', band: 'neutral' };
    if (score >= 80) return { label: 'CRITICAL', band: 'critical' };
    if (score >= 60) return { label: 'HIGH', band: 'high' };
    if (score >= 40) return { label: 'MEDIUM', band: 'medium' };
    if (score >= 20) return { label: 'LOW', band: 'low' };
    return { label: 'HEALTHY', band: 'low' };
}

function vulnDominantBand({ critical = 0, high = 0, medium = 0, low = 0 }) {
    if (critical > 0) return 'critical';
    if (high > 0) return 'high';
    if (medium > 0) return 'medium';
    if (low > 0) return 'low';
    return 'neutral';
}

function priorityTone(priority) {
    if (!priority) return 'info';
    const p = String(priority).toLowerCase();
    if (p.includes('critical') || p.includes('p0')) return 'critical';
    if (p.includes('high') || p.includes('p1')) return 'high';
    if (p.includes('medium') || p.includes('p2')) return 'medium';
    if (p.includes('low') || p.includes('p3')) return 'low';
    return 'info';
}

// ---------- Findings tabs ----------

// PRD-45.5: Updated exploitability enum mapping
function exploitabilityTone(exploitability) {
    if (!exploitability) return null;
    const e = String(exploitability).toLowerCase();
    if (e === 'cisa-kev' || e.includes('actively') || e.includes('kev')) return 'critical';
    if (e === 'epss-high' || e.includes('high-epss') || e.includes('high')) return 'high';
    if (e === 'epss-medium' || e.includes('medium') || e.includes('moderate')) return 'medium';
    if (e === 'epss-low' || e.includes('low')) return 'low';
    if (e === 'no-known-exploit') return 'info';
    return 'info';
}

// PRD-45.5: Human-readable exploitability label
function exploitabilityLabel(val) {
    if (!val) return null;
    const labels = {
        'cisa-kev': 'CISA KEV',
        'epss-high': 'EPSS High',
        'epss-medium': 'EPSS Medium',
        'epss-low': 'EPSS Low',
        'no-known-exploit': 'No Known Exploit'
    };
    return labels[val] || val;
}

function RemediationFullView({ recommendations }) {
    const list = Array.isArray(recommendations) ? recommendations : [];
    if (list.length === 0) return <div className="v3-empty">No prioritized remediations available.</div>;
    return (
        <div className="v3-rem-list">
            {list.map((rec, i) => {
                // Per PRD-43 §"V3 Data Mapping Gaps": vulnerability is the canonical title field
                const title = rec.vulnerability || rec.title || `Finding ${i + 1}`;
                const priority = rec.priority || null;
                const impact = rec.impact || null;
                const exploitability = rec.exploitability || null;
                const attackVector = rec.attackVector || null;
                const remediation = rec.remediation || null;
                const effort = rec.effort || null;
                const timeline = rec.timeline || null;
                const cves = Array.isArray(rec.cves) ? rec.cves : [];
                const rank = rec.rank != null ? rec.rank : i + 1;
                const isEolPromoted = rec.source === 'eol-auto-promoted';
                return (
                    <div key={i} className={`v3-rem-item ${isEolPromoted ? 'v3-rem-item--eol' : ''}`}>
                        <div className="v3-rem-header">
                            <div className="v3-rem-title">
                                <span className="v3-rem-rank">#{rank}</span> {title}
                                {isEolPromoted && (
                                    <span className="v3-badge v3-badge--eol" title="Auto-promoted from end-of-life technology analysis">EOL</span>
                                )}
                            </div>
                            {priority && (
                                <span className={`v3-tag v3-tag--${priorityTone(priority)}`}>{priority}</span>
                            )}
                        </div>
                        {attackVector && <div className="v3-rem-field"><span>Attack vector:</span> {attackVector}</div>}
                        {remediation && <div className="v3-rem-field"><span>Remediation:</span> {remediation}</div>}
                        <div className="v3-rem-meta">
                            {impact && (
                                <span className={`v3-chip v3-chip--${priorityTone(impact)}`}>Impact: {impact}</span>
                            )}
                            {exploitability && (
                                <span className={`v3-chip v3-chip--${exploitabilityTone(exploitability)}`}>
                                    {exploitabilityLabel(exploitability) || exploitability}
                                </span>
                            )}
                            {effort && <span className="v3-chip">Effort: {effort}</span>}
                            {timeline && <span className="v3-chip">Timeline: {timeline}</span>}
                            {cves.length > 0 && (
                                <span className="v3-chip v3-chip--cve">
                                    {cves.slice(0, 4).join(', ')}{cves.length > 4 ? ` +${cves.length - 4}` : ''}
                                </span>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/**
 * Normalize a single defensive recommendation item into a canonical shape.
 * Per PRD-43 §"V3 Data Mapping Gaps" #3: Claude generates inconsistent shapes —
 * N8N schema says {layer, control, priority}, production frontend reads
 * {category, recommendation, priority, effort}, and some items are plain strings.
 */
function normalizeDefensiveItem(item) {
    if (typeof item === 'string') {
        return { category: 'General', text: item, priority: null, effort: null };
    }
    if (!item || typeof item !== 'object') return null;
    return {
        category: item.category || item.layer || 'General',
        text: item.recommendation || item.control || item.text || item.description || '',
        priority: item.priority || null,
        effort: item.effort || null
    };
}

function HardeningFullView({ defensive }) {
    if (!defensive) return <div className="v3-empty">No hardening recommendations available.</div>;
    const list = (Array.isArray(defensive) ? defensive : []).map(normalizeDefensiveItem).filter(Boolean);
    if (list.length === 0) return <div className="v3-empty">No hardening recommendations available.</div>;

    // Group by category for scannable rendering
    const grouped = list.reduce((acc, item) => {
        const key = item.category || 'General';
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
    }, {});

    return (
        <div className="v3-hardening-grid">
            {Object.entries(grouped).map(([category, items]) => (
                <div key={category} className="v3-hardening-card">
                    <h4>{category}</h4>
                    <ul>
                        {items.map((item, j) => (
                            <li key={j}>
                                {item.text || '(empty)'}
                                {(item.priority || item.effort) && (
                                    <span className="v3-hardening-meta">
                                        {item.priority && (
                                            <span className={`v3-chip v3-chip--${priorityTone(item.priority)}`}>{item.priority}</span>
                                        )}
                                        {item.effort && <span className="v3-chip">Effort: {item.effort}</span>}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
}

// ---------- Intelligence tabs ----------

// Per PRD-43 §"V3 Data Mapping Gaps" #4: real phase keys are snake_cased with
// numeric prefixes, and each phase is an object with multiple labeled sub-arrays.
// Structure verified from AssetDetails.js:3580-3686 + N8N schema.
const PENTEST_PHASES = [
    {
        key: 'phase1_reconnaissance',
        number: 1,
        title: 'Reconnaissance',
        sections: [
            { field: 'completed', label: 'Completed', icon: '✓', className: 'done' },
            { field: 'nextSteps', label: 'Next Steps', icon: '•' }
        ]
    },
    {
        key: 'phase2_scanning',
        number: 2,
        title: 'Scanning & Enumeration',
        sections: [
            { field: 'completed', label: 'Completed', icon: '✓', className: 'done' },
            { field: 'nextSteps', label: 'Next Steps', icon: '•' }
        ]
    },
    {
        key: 'phase3_exploitation',
        number: 3,
        title: 'Exploitation',
        sections: [
            { field: 'primaryTargets', label: 'Primary Targets', icon: '🎯' },
            { field: 'techniques', label: 'Techniques', icon: '•' },
            { field: 'tools', label: 'Tools', icon: '•' }
        ]
    },
    {
        key: 'phase4_postExploitation',
        number: 4,
        title: 'Post-Exploitation',
        sections: [
            { field: 'lateralMovement', label: 'Lateral Movement', icon: '→' },
            { field: 'persistence', label: 'Persistence', icon: '•' },
            { field: 'dataExfiltration', label: 'Data Exfiltration', icon: '•' },
            { field: 'objectives', label: 'Objectives', icon: '•' }
        ]
    }
];

function PentestView({ roadmap }) {
    if (!roadmap || (typeof roadmap === 'object' && Object.keys(roadmap).length === 0)) {
        return (
            <div className="v3-empty">
                No pentest roadmap available for this asset.
                <div className="v3-empty-hint">Pentest roadmaps are generated by enrichment when the asset has enough discovery data.</div>
            </div>
        );
    }

    const rendered = PENTEST_PHASES.map((phaseDef) => {
        const phase = roadmap[phaseDef.key];
        if (!phase || typeof phase !== 'object') return null;

        // Collect populated sections only
        const populatedSections = phaseDef.sections
            .map((s) => ({ ...s, items: Array.isArray(phase[s.field]) ? phase[s.field] : [] }))
            .filter((s) => s.items.length > 0);

        if (populatedSections.length === 0) return null;

        return (
            <div key={phaseDef.key} className={`v3-phase v3-phase--${phaseDef.number}`}>
                <h4>
                    <span className="v3-phase-num">Phase {phaseDef.number}</span> {phaseDef.title}
                </h4>
                {populatedSections.map((section) => (
                    <div key={section.field} className={`v3-phase-section ${section.className || ''}`}>
                        <div className="v3-phase-section-label">{section.label}</div>
                        <ul>
                            {section.items.map((item, j) => (
                                <li key={j}>
                                    <span className="v3-phase-icon">{section.icon}</span>{' '}
                                    {typeof item === 'string' ? item : JSON.stringify(item)}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        );
    }).filter(Boolean);

    if (rendered.length === 0) return <div className="v3-empty">No pentest roadmap phases available.</div>;
    return <div className="v3-phases">{rendered}</div>;
}

// Per PRD-43 §"V3 Data Mapping Gaps" #5: each framework is an array of objects
// with framework-specific fields. Shape verified from AssetDetails.js:3707-3747.
function ComplianceFrameworkCard({ title, toneClass, items, headerField, gapsField }) {
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
        <div className={`v3-framework ${toneClass}`}>
            <h4>{title}</h4>
            <div className="v3-framework-items">
                {items.map((item, i) => {
                    // NIST has {function, category}; others have {category} or {control}
                    let header;
                    if (headerField === '__nist') {
                        header = [item.function, item.category].filter(Boolean).join(' — ');
                    } else {
                        header = item[headerField] || '(unnamed)';
                    }
                    const gaps = Array.isArray(item[gapsField]) ? item[gapsField] : [];
                    const severity = item.severity || null;
                    return (
                        <div key={i} className="v3-framework-item">
                            <div className="v3-framework-item-header">
                                <span className="v3-framework-item-title">{header}</span>
                                {severity && (
                                    <span className={`v3-chip v3-chip--${priorityTone(severity)}`}>{severity}</span>
                                )}
                            </div>
                            {gaps.length > 0 && (
                                <ul className="v3-framework-gaps">
                                    {gaps.map((g, j) => <li key={j}>{g}</li>)}
                                </ul>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function ComplianceView({ compliance }) {
    if (!compliance) return <div className="v3-empty">No compliance mapping available.</div>;

    const owasp = Array.isArray(compliance.owaspTop10) ? compliance.owaspTop10 : [];
    const cis = Array.isArray(compliance.cisControls) ? compliance.cisControls : [];
    const nist = Array.isArray(compliance.nistCsf) ? compliance.nistCsf : [];

    if (owasp.length === 0 && cis.length === 0 && nist.length === 0) {
        return <div className="v3-empty">No compliance mapping available.</div>;
    }

    return (
        <div className="v3-compliance">
            <ComplianceFrameworkCard
                title="OWASP Top 10"
                toneClass="v3-framework--owasp"
                items={owasp}
                headerField="category"
                gapsField="findings"
            />
            <ComplianceFrameworkCard
                title="CIS Controls"
                toneClass="v3-framework--cis"
                items={cis}
                headerField="control"
                gapsField="gaps"
            />
            <ComplianceFrameworkCard
                title="NIST CSF"
                toneClass="v3-framework--nist"
                items={nist}
                headerField="__nist"
                gapsField="gaps"
            />
        </div>
    );
}

// Per PRD-43 §"V3 Data Mapping Gaps" #6: each entry is {technique, name, tactic,
// evidence, exploitability}. `technique` is the ID (e.g. "T1595"), NOT the name.
// Shape verified from AssetDetails.js:3540-3552 + N8N schema.
function MitreView({ mitre }) {
    const list = Array.isArray(mitre) ? mitre : [];
    if (list.length === 0) return <div className="v3-empty">No MITRE ATT&amp;CK mapping available.</div>;

    // Unique tactic badges for the header row
    const tactics = [...new Set(list.map((m) => m.tactic).filter(Boolean))];

    return (
        <div>
            {tactics.length > 0 && (
                <div className="v3-mitre-tactics">
                    {tactics.map((t, i) => (
                        <span key={i} className="v3-mitre-tactic-badge">{t}</span>
                    ))}
                </div>
            )}
            <div className="v3-mitre-wrap">
                <table className="v3-mitre-table">
                    <thead>
                        <tr>
                            <th>Tactic</th>
                            <th>Technique</th>
                            <th>ID</th>
                            <th>Evidence</th>
                            <th>Exploitability</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((t, i) => (
                            <tr key={i}>
                                <td>{t.tactic || '—'}</td>
                                <td>{t.name || '—'}</td>
                                <td>{t.technique ? <code>{t.technique}</code> : '—'}</td>
                                <td className="v3-mitre-evidence">{t.evidence || '—'}</td>
                                <td>
                                    {t.exploitability ? (
                                        <span className={`v3-chip v3-chip--${priorityTone(t.exploitability)}`}>
                                            {t.exploitability}
                                        </span>
                                    ) : '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------- History tabs ----------

// PRD-54 Phase D: Quick vs Deep tier labels disambiguate the daily/weekly
// scans that contribute different slices to the same risk picture. Daily
// (vuln-scan / discovery) is "Quick"; weekly (deep-security) is "Deep".
const SCAN_TIER = {
    'vuln-scan': 'Quick',
    'vuln-scan-ip': 'Quick',
    'discovery': 'Quick',
    'discovery-ip': 'Quick',
    'deep-security': 'Deep',
    'deep-security-ip': 'Deep',
};

function ScanHistoryView({ history, asset }) {
    const entries = history?.scans || [];
    if (!entries.length) return <div className="v3-empty">No scan history yet.</div>;

    // Asset-scoped WAF-limited chip (v1 simplification — matches the
    // ScanDetailsTab select-suffix pattern). Every history row gets the chip
    // when the asset is currently in a WAF-blocked state. Per-scan plumbing
    // is deferred; current WAF state is the read-out.
    const wafFraction = typeof asset?.wafBlockingDisplayFraction === 'number'
        ? asset.wafBlockingDisplayFraction
        : (typeof asset?.wafBlockingFraction === 'number' ? asset.wafBlockingFraction : 0);
    const wafSampleSize = asset?.wafBlockingSampleSize || 0;
    const wafLimited = wafFraction > 0.5 && wafSampleSize >= 10;
    const wafVendor = wafVendorLabel(asset?.wafBlockingDisplayType || asset?.wafBlockingType);
    const wafPct = Math.round(wafFraction * 100);
    const wafSample = wafSampleSize.toLocaleString();
    const wafTooltip = wafLimited
        ? `${wafVendor} WAF blocked ${wafPct}% of ${wafSample} probes — findings reflect WAF-bypassable surface only`
        : null;

    return (
        <div className="v3-history-list">
            {entries.slice(0, 50).map((h, i) => {
                const tier = SCAN_TIER[h.scanType] || null;
                return (
                    <div key={i} className="v3-history-row">
                        <span className="v3-history-date">{fmtDate(h.completedAt)}</span>
                        <span className="v3-history-type">
                            {tier && (
                                <span className={`v3-history-tier v3-history-tier--${tier.toLowerCase()}`}>
                                    {tier}
                                </span>
                            )}
                            {h.scanType || '—'}
                        </span>
                        <span className="v3-history-level">{h.riskLevel || '—'}</span>
                        <span className="v3-history-counts">
                            {h.vulnCritical}C · {h.vulnHigh}H · {h.vulnMedium}M · {h.vulnLow}L
                            {wafLimited && (
                                <span
                                    className="v3-history-waf-badge"
                                    title={wafTooltip}
                                    aria-label={wafTooltip}
                                >
                                    WAF-limited
                                </span>
                            )}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

// Per PRD-43 §"V3 Data Mapping Gaps" #8: changesSinceLastAnalysis was dropped
// from V3 entirely. Lives in the Trends sub-tab now so the narrative has the
// chart for context. Shape verified from AssetDetails.js:3937-3986.
function ChangesSinceLastAnalysisBlock({ changes }) {
    if (!changes) return null;
    const hasContent = changes.summary ||
        changes.riskTrendNarrative ||
        (Array.isArray(changes.newFindings) && changes.newFindings.length > 0) ||
        (Array.isArray(changes.resolvedFindings) && changes.resolvedFindings.length > 0);
    if (!hasContent) return null;

    const newList = Array.isArray(changes.newFindings) ? changes.newFindings : [];
    const resolvedList = Array.isArray(changes.resolvedFindings) ? changes.resolvedFindings : [];

    const renderFinding = (f) =>
        typeof f === 'string' ? f : (f?.title || f?.description || JSON.stringify(f));

    return (
        <div className="v3-trends-card v3-changes">
            <h4>Changes Since Last Analysis</h4>
            {changes.summary && <p className="v3-intel-text">{changes.summary}</p>}
            {changes.riskTrendNarrative && (
                <p className="v3-intel-text v3-intel-text--trend">
                    <strong>Trend:</strong> {changes.riskTrendNarrative}
                </p>
            )}
            <div className="v3-changes-grid">
                {newList.length > 0 && (
                    <div className="v3-changes-col v3-changes-col--new">
                        <h5>New Findings ({newList.length})</h5>
                        <ul>
                            {newList.map((f, i) => <li key={i}>{renderFinding(f)}</li>)}
                        </ul>
                    </div>
                )}
                {resolvedList.length > 0 && (
                    <div className="v3-changes-col v3-changes-col--resolved">
                        <h5>Resolved Findings ({resolvedList.length})</h5>
                        <ul>
                            {resolvedList.map((f, i) => <li key={i}>{renderFinding(f)}</li>)}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}

function TrendsView({ history, trendData, findingsTrendData = [], enrichment, onPointClick, attribution, onRetryAttribution }) {
    const changes = enrichment?.aiAnalysis?.changesSinceLastAnalysis;
    return (
        <div className="v3-trends">
            <div className="v3-trends-card">
                <h4>Risk Score Trend</h4>
                <p className="v3-hint">
                    Higher score = higher risk. Updated when findings are added or resolved.
                    Click a point to see what changed.
                </p>
                <RiskTrendChart data={trendData} onPointClick={onPointClick} />
                {attribution && (
                    <ScoreAttributionPanel
                        attribution={attribution.data}
                        loading={attribution.loading}
                        error={attribution.error}
                        onRetry={onRetryAttribution}
                    />
                )}
            </div>
            <ChangesSinceLastAnalysisBlock changes={changes} />
            <div className="v3-trends-card">
                <h4>Findings Trend</h4>
                {findingsTrendData.length > 0 ? (
                    <DriftFindingsTrend data={findingsTrendData} />
                ) : (
                    <div className="v3-empty">Not enough scan history yet.</div>
                )}
            </div>
        </div>
    );
}

function DriftView({ assetId, history }) {
    const entries = history?.scans || [];
    return (
        <div className="v3-drift">
            <DriftTimeline assetId={assetId} history={entries} />
            <div className="v3-drift-events-wrap">
                <DriftEventsList assetId={assetId} />
            </div>
        </div>
    );
}

function RawDataView({ asset, assetId }) {
    const discoveryResultId = asset?.lastDiscoveryResultId || asset?.lastResultId;
    const vulnScanResultId = asset?.lastVulnScanResultId;
    const deepSecurityResultId = asset?.lastDeepSecurityResultId;
    const [downloading, setDownloading] = useState(null);

    // Downloads are keyed by (sectionKey, fileName) so two sections can have a
    // same-named file (all sections include all_urls.txt) without spinner collision.
    const handleDownload = useCallback(async (resultId, fileName, downloadKey) => {
        if (!resultId) return;
        setDownloading(downloadKey);
        try { await downloadAssetFile(assetId, resultId, fileName); }
        catch (err) { console.error('Download failed:', err); }
        finally { setDownloading(null); }
    }, [assetId]);

    const handleRaw = useCallback(async (resultId, downloadKey) => {
        if (!resultId) return;
        setDownloading(downloadKey);
        try {
            const url = await getAssetRawDataUrl(assetId, resultId);
            if (url) window.open(url, '_blank', 'noopener');
        } catch (err) { console.error('Raw download failed:', err); }
        finally { setDownloading(null); }
    }, [assetId]);

    if (!discoveryResultId && !vulnScanResultId && !deepSecurityResultId) {
        return <div className="v3-empty">No scan results to download yet.</div>;
    }

    // PRD-65 Phase 3: separate sections per scan type so vuln-scan URL-list files
    // (prioritized/dropped/all) surface alongside discovery artifacts. Each section's
    // blob is uploaded by its own pipeline and keyed on its own resultId — they
    // never co-mingle in a single zip.
    const sections = [
        {
            key: 'discovery',
            title: 'Discovery artifacts',
            resultId: discoveryResultId,
            files: [
                { file: 'live_subdomains.txt',     label: 'Live subdomains' },
                { file: 'subdomains_all.txt',      label: 'All subdomains' },
                { file: 'dns_records.jsonl',       label: 'DNS records (JSONL)' },
                { file: 'live_urls.txt',           label: 'Live URLs' },
                { file: 'crawled_urls.txt',        label: 'Crawled URLs' },
                { file: 'api_urls.txt',            label: 'API URLs' },
                { file: 'interesting_urls.txt',    label: 'Interesting URLs' },
                { file: 'naabu_results.jsonl',     label: 'Port scan results (naabu)' },
                { file: 'nmap_services.json',      label: 'Services (nmap)' },
                { file: 'technology_summary.json', label: 'Technology summary' },
                { file: 'cloud_enum.json',         label: 'Cloud enumeration' }
            ]
        },
        {
            key: 'vuln-scan',
            title: 'Vulnerability scan artifacts',
            resultId: vulnScanResultId,
            files: [
                { file: 'prioritized_urls.txt', label: 'Prioritized URLs (scanned)' },
                { file: 'dropped_urls.txt',     label: 'Dropped URLs (not scanned)' },
                { file: 'all_urls.txt',         label: 'All discovered URLs' }
            ]
        },
        {
            key: 'deep-security',
            title: 'Deep security artifacts',
            resultId: deepSecurityResultId,
            files: [
                { file: 'prioritized_urls.txt', label: 'Prioritized URLs (scanned)' },
                { file: 'dropped_urls.txt',     label: 'Dropped URLs (not scanned)' },
                { file: 'all_urls.txt',         label: 'All discovered URLs' }
            ]
        }
    ].filter(s => s.resultId);

    return (
        <div className="v3-raw">
            {sections.map(({ key, title, resultId, files }) => {
                const rawKey = `${key}:raw`;
                return (
                    <div key={key} className="v3-raw-section">
                        <h4 className="v3-raw-section-title">{title}</h4>
                        <button
                            type="button"
                            className="v3-raw-btn v3-raw-btn--primary"
                            onClick={() => handleRaw(resultId, rawKey)}
                            disabled={downloading === rawKey}
                        >
                            {downloading === rawKey ? 'Preparing…' : 'Download full scan blob (JSON)'}
                        </button>
                        <div className="v3-raw-grid">
                            {files.map(({ file, label }) => {
                                const dlKey = `${key}:${file}`;
                                return (
                                    <button
                                        key={dlKey}
                                        type="button"
                                        className="v3-raw-btn"
                                        onClick={() => handleDownload(resultId, file, dlKey)}
                                        disabled={downloading === dlKey}
                                    >
                                        {downloading === dlKey ? 'Downloading…' : label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ---------- PRD-45.5: Wired-up dead fields ----------

function TrendAnalysisView({ trendAnalysis }) {
    if (!trendAnalysis) return <div className="v3-empty">No trend analysis available.</div>;
    const direction = trendAnalysis.direction || 'stable';
    const directionIcon = direction === 'improving' ? '↗' : direction === 'declining' ? '↘' : '→';
    const directionTone = direction === 'improving' ? 'low' : direction === 'declining' ? 'high' : 'info';

    return (
        <div className="v3-trends">
            <div className="v3-trends-card">
                <h4>
                    AI Trend Analysis{' '}
                    <span className={`v3-chip v3-chip--${directionTone}`}>
                        {directionIcon} {direction}{trendAnalysis.riskDelta != null ? ` (${trendAnalysis.riskDelta > 0 ? '+' : ''}${trendAnalysis.riskDelta})` : ''}
                    </span>
                </h4>
                {trendAnalysis.projectedRisk && (
                    <p className="v3-intel-text"><strong>Projected risk:</strong> {trendAnalysis.projectedRisk}</p>
                )}
                {Array.isArray(trendAnalysis.significantChanges) && trendAnalysis.significantChanges.length > 0 && (
                    <div className="v3-tech-block">
                        <h5>Significant Changes</h5>
                        <ul className="v3-bullet-list">
                            {trendAnalysis.significantChanges.map((c, i) => (
                                <li key={i}>{typeof c === 'string' ? c : JSON.stringify(c)}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {Array.isArray(trendAnalysis.recommendations) && trendAnalysis.recommendations.length > 0 && (
                    <div className="v3-tech-block">
                        <h5>Recommendations</h5>
                        <ul className="v3-bullet-list">
                            {trendAnalysis.recommendations.map((r, i) => (
                                <li key={i}>{typeof r === 'string' ? r : JSON.stringify(r)}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}

function CloudSecurityView({ cloudFindings }) {
    if (!cloudFindings) return <div className="v3-empty">No cloud security findings available.</div>;
    const misconfigs = Array.isArray(cloudFindings.misconfigurations) ? cloudFindings.misconfigurations : [];
    const exposures = Array.isArray(cloudFindings.publicExposures) ? cloudFindings.publicExposures : [];
    const gaps = Array.isArray(cloudFindings.complianceGaps) ? cloudFindings.complianceGaps : [];
    if (misconfigs.length === 0 && exposures.length === 0 && gaps.length === 0) {
        return <div className="v3-empty">No cloud security findings detected.</div>;
    }
    const renderList = (title, items, tone) => items.length > 0 && (
        <div className="v3-attack-card">
            <h4>{title} <span className={`v3-chip v3-chip--${tone}`}>{items.length}</span></h4>
            <ul>
                {items.map((item, i) => (
                    <li key={i}>{typeof item === 'string' ? item : (item.description || item.name || JSON.stringify(item))}</li>
                ))}
            </ul>
        </div>
    );
    return (
        <div className="v3-attack-grid">
            {renderList('Misconfigurations', misconfigs, 'high')}
            {renderList('Public Exposures', exposures, 'critical')}
            {renderList('Compliance Gaps', gaps, 'medium')}
        </div>
    );
}

// ---------- Technologies tab: merged data + analysis ----------

// Per PRD-43 §"V3 Data Mapping Gaps" #7: real technologyAnalysis has
// stackOverview + riskAssessment + architectureInsights + techHealthScore +
// eolTechnologies + versionRisks. EOL items have rich fields. Shape verified
// from AssetDetails.js:3837-3930 + N8N schema + PRD-42 Session 3 test blob.
function TechnologiesView({ asset, assetId, enrichment, suppressions, onSuppressionsChange }) {
    const tech = enrichment?.aiAnalysis?.technologyAnalysis;
    const eolList = Array.isArray(tech?.eolTechnologies) ? tech.eolTechnologies : [];
    const versionRisks = Array.isArray(tech?.versionRisks) ? tech.versionRisks : [];

    return (
        <div className="v3-tech-view">
            {tech && (
                <div className="v3-tech-analysis">
                    <div className="v3-tech-analysis-header">
                        <h4>AI Technology Analysis</h4>
                        {tech.techHealthScore != null && (
                            <span className="v3-tech-health">
                                Health <strong>{tech.techHealthScore}</strong>/100
                            </span>
                        )}
                    </div>

                    {tech.stackOverview && (
                        <div className="v3-tech-block">
                            <h5>Stack Overview</h5>
                            <p className="v3-intel-text">{tech.stackOverview}</p>
                        </div>
                    )}
                    {tech.riskAssessment && (
                        <div className="v3-tech-block">
                            <h5>Risk Assessment</h5>
                            <p className="v3-intel-text">{tech.riskAssessment}</p>
                        </div>
                    )}
                    {tech.architectureInsights && (
                        <div className="v3-tech-block">
                            <h5>Architecture Insights</h5>
                            <p className="v3-intel-text">{tech.architectureInsights}</p>
                        </div>
                    )}

                    {eolList.length > 0 && (
                        <div className="v3-tech-block">
                            <h5>End-of-Life / Critical Technologies ({eolList.length})</h5>
                            <div className="v3-eol-grid">
                                {eolList.map((t, i) => {
                                    if (typeof t === 'string') {
                                        return <div key={i} className="v3-eol-card"><div className="v3-eol-name">{t}</div></div>;
                                    }
                                    const name = t?.name || t?.technology || 'Unknown';
                                    const severity = t?.severity || null;
                                    const cves = Array.isArray(t?.knownCVEs) ? t.knownCVEs : [];
                                    return (
                                        <div key={i} className={`v3-eol-card v3-eol-card--${priorityTone(severity)}`}>
                                            <div className="v3-eol-header">
                                                <span className="v3-eol-name">{name}</span>
                                                {severity && (
                                                    <span className={`v3-chip v3-chip--${priorityTone(severity)}`}>{severity}</span>
                                                )}
                                            </div>
                                            {t?.remediation && (
                                                <div className="v3-eol-field">
                                                    <span>Remediation:</span> {t.remediation}
                                                </div>
                                            )}
                                            {t?.businessImpact && (
                                                <div className="v3-eol-field">
                                                    <span>Business Impact:</span> {t.businessImpact}
                                                </div>
                                            )}
                                            {cves.length > 0 && (
                                                <div className="v3-eol-cves">
                                                    <span>Known CVEs:</span>{' '}
                                                    {cves.slice(0, 5).map((cve, ci) => (
                                                        <code key={ci} className="v3-cve-chip">{cve}</code>
                                                    ))}
                                                    {cves.length > 5 && (
                                                        <span className="v3-cve-more">+{cves.length - 5}</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {versionRisks.length > 0 && (
                        <div className="v3-tech-block">
                            <h5>Version Risks ({versionRisks.length})</h5>
                            <div className="v3-version-grid">
                                {versionRisks.map((v, i) => {
                                    const cves = Array.isArray(v?.knownCVEs) ? v.knownCVEs : [];
                                    return (
                                        <div key={i} className="v3-version-card">
                                            <div className="v3-version-header">
                                                <span className="v3-version-name">{v?.technology || 'Unknown'}</span>
                                                <span className="v3-version-chase">
                                                    {v?.currentVersion || '?'}
                                                    {v?.latestVersion && <> → {v.latestVersion}</>}
                                                </span>
                                            </div>
                                            {v?.recommendation && (
                                                <div className="v3-version-rec">{v.recommendation}</div>
                                            )}
                                            {cves.length > 0 && (
                                                <div className="v3-eol-cves">
                                                    {cves.slice(0, 5).map((cve, ci) => (
                                                        <code key={ci} className="v3-cve-chip">{cve}</code>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}
            <div className="v3-tech-inventory">
                <AssetDataSections
                    asset={asset}
                    assetId={assetId}
                    enrichment={enrichment}
                    suppressions={suppressions}
                    onSuppressionsChange={onSuppressionsChange}
                    visibleSections={['technologies']}
                    autoExpand={true}
                    hideHeaders={true}
                />
            </div>
        </div>
    );
}

// ---------- 2-level tab system config ----------

const TAB_GROUPS = [
    {
        id: 'discovery',
        label: 'Discovery',
        subtitle: "What's out there",
        tabs: [
            { id: 'subdomains',   label: 'Subdomains',   section: ['subdomains', 'stale'] },
            { id: 'ips',          label: 'IPs',          section: ['ips'] },
            { id: 'ports',        label: 'Ports',        section: ['ports'] },
            { id: 'urls',         label: 'URLs',         section: ['urls'] },
            { id: 'technologies', label: 'Technologies', section: null /* custom view */ },
            { id: 'cloud',        label: 'Cloud',        section: ['cloud'] },
            { id: 'registration', label: 'Registration', section: ['registration'], domainOnly: true }
        ]
    },
    {
        id: 'findings',
        label: 'Findings',
        subtitle: "What's wrong",
        tabs: [
            { id: 'vulnerabilities', label: 'Vulnerabilities', section: ['vulnerabilities'] },
            { id: 'remediation',     label: 'Remediation' },
            { id: 'hardening',       label: 'Hardening' }
        ]
    },
    {
        id: 'intelligence',
        label: 'Intelligence',
        subtitle: 'How we think about it',
        tabs: [
            { id: 'mitre',          label: 'MITRE ATT&CK' },
            { id: 'pentest',        label: 'Pentest Roadmap' },
            { id: 'compliance',     label: 'Compliance' },
            { id: 'trend-analysis', label: 'Trend Analysis' },
            { id: 'cloud-security', label: 'Cloud Security' }
        ]
    },
    {
        id: 'history',
        label: 'History',
        subtitle: 'What changed · raw data',
        tabs: [
            { id: 'scan-history',  label: 'Scan History' },
            { id: 'trends',        label: 'Trends' },
            { id: 'drift',         label: 'Drift' },
            { id: 'scan-details',  label: 'Scan Details' },
            { id: 'raw-data',      label: 'Raw Data' },
            { id: 'deep-security', label: 'Deep Security', conditional: 'deepSec' }
        ]
    }
];

// ---------- Main component ----------

function AssetDetails() {
    const { assetId } = useParams();
    const navigate = useNavigate();

    const [asset, setAsset] = useState(null);
    const [enrichment, setEnrichment] = useState(null);
    const [scoreBreakdown, setScoreBreakdown] = useState(null);
    const [showBreakdown, setShowBreakdown] = useState(false);
    const [breakdownError, setBreakdownError] = useState(null); // PRD-133 L4: visible fetch-error state
    const [attribution, setAttribution] = useState(null); // PRD-133 WP5: { loading, error, data, atTs }
    const [history, setHistory] = useState(null);
    const [timeline, setTimeline] = useState(null);
    const [suppressions, setSuppressions] = useState([]);

    // PRD-63 WP3: optimistic risk-card refresh. Unified modals return an
    // assetRisk payload (from formatAssetRiskResponse) that we merge into the
    // asset object so the score / level / scanPenalty render updates without
    // a full reload. The asset list and other consumers refetch on next nav.
    const handleAssetRiskChange = useCallback((assetRisk) => {
        if (!assetRisk) return;
        setAsset(prev => prev ? ({
            ...prev,
            currentRiskScore: assetRisk.score ?? prev.currentRiskScore,
            currentRiskLevel: assetRisk.level ?? prev.currentRiskLevel,
            scanPenalty: assetRisk.scanPenalty ?? prev.scanPenalty,
            // assetRisk.counts carries severity counts when present
            activeVulnCount: assetRisk.counts?.total ?? prev.activeVulnCount,
            riskRecomputedAt: new Date().toISOString(),
        }) : prev);
    }, []);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [scanningType, setScanningType] = useState(null);
    const [activeGroup, setActiveGroup] = useState('discovery');
    const [activeSub, setActiveSub] = useState('subdomains');
    const [driftOpenCount, setDriftOpenCount] = useState(0);
    const [scanToast, setScanToast] = useState(null);
    const [currentTier, setCurrentTier] = useState(null);

    const loadAsset = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const [assetResp, historyResp] = await Promise.all([
                getAsset(assetId),
                getAssetHistory(assetId).catch(() => null)
            ]);
            setAsset(assetResp.asset);
            setEnrichment(assetResp.enrichment || null);
            setHistory(historyResp);
        } catch (err) {
            setError(err.message || 'Failed to load asset');
        } finally {
            setLoading(false);
        }
    }, [assetId]);

    useEffect(() => { loadAsset(); }, [loadAsset]);

    // PRD-65 Phase 3: cached tier drives CoverageBanner copy ("Upgrade to Pro for 200 URLs").
    // Failure is silent — banner just degrades to no upsell CTA.
    useEffect(() => {
        (async () => {
            try {
                const info = await getCachedTierInfo();
                if (info?.tier) setCurrentTier(info.tier);
            } catch (err) { console.log('Tier info unavailable:', err.message); }
        })();
    }, []);

    useEffect(() => {
        if (!assetId) return;
        (async () => {
            try {
                const result = await getAssetSuppressions(assetId);
                if (result?.success && result.suppressions) setSuppressions(result.suppressions);
            } catch (err) { console.log('Suppressions unavailable:', err.message); }
        })();
    }, [assetId]);

    useEffect(() => {
        if (!assetId) return;
        (async () => {
            try {
                const raw = await getEvents(assetId);
                const list = Array.isArray(raw) ? raw : (raw?.events || []);
                const open = list.filter((e) => e.status === 'open' || e.status === 'acknowledged').length;
                setDriftOpenCount(open);
            } catch (err) { console.log('Drift events unavailable:', err.message); }
        })();
    }, [assetId]);

    // PRD-59 Tier 2: Fetch cumulative risk timeline in parallel with asset+history.
    // Source of truth for both the Risk Score line and Findings Trend area chart.
    useEffect(() => {
        if (!assetId) return;
        (async () => {
            try {
                const resp = await getRiskTimeline(assetId);
                setTimeline(resp || null);
            } catch (err) {
                console.log('Risk timeline unavailable:', err.message);
                setTimeline(null);
            }
        })();
    }, [assetId]);

    // PRD-59 Tier 2: Risk Score Trend is now sourced directly from the
    // cumulativeRiskTimeline endpoint, not derived from per-scan history.
    // cumulativeRiskTimeline is post-PRD-51 by construction; all rows are
    // higher=riskier and step-stable (only changes when a finding lands or
    // resolves), so we plot `score` as-is with no per-scan weighting.
    const trendData = useMemo(() => {
        if (!timeline?.timeline?.length) return [];
        return timeline.timeline.map((row) => ({
            date: row.ts,
            score: row.score,
            level: row.level,
            critical: row.vulnCritical,
            high: row.vulnHigh,
            medium: row.vulnMedium,
            low: row.vulnLow,
            eventType: row.eventType,
            triggerScanType: row.triggerScanType
        }));
    }, [timeline]);

    // PRD-59 Tier 2: Findings Trend (stacked area) reads from the same
    // cumulativeRiskTimeline source so the two charts can never disagree.
    const findingsTrendData = useMemo(() => {
        if (!timeline?.timeline?.length) return [];
        return timeline.timeline.map((row) => ({
            date: row.ts,
            critical: row.vulnCritical || 0,
            high: row.vulnHigh || 0,
            medium: row.vulnMedium || 0,
            low: row.vulnLow || 0
        }));
    }, [timeline]);

    // Scan + export handlers
    const handleScan = useCallback(async (scanType) => {
        setScanningType(scanType);
        setScanToast(null);
        try {
            const res = await triggerAssetScan(assetId, scanType);
            setScanToast({ kind: 'success', msg: res?.message || `${scanType} scheduled.` });
            setTimeout(() => loadAsset(), 600);
        } catch (err) {
            setScanToast({ kind: 'error', msg: err.message || 'Failed to schedule scan' });
        } finally {
            setScanningType(null);
        }
    }, [assetId, loadAsset]);

    // Retained but unused: the Export button (PDF/CSV/JSON) is hidden until the
    // export feature is actually built. Re-enable by passing onExport to QuickActions.
    // eslint-disable-next-line no-unused-vars
    const handleExport = useCallback(async (format) => {
        if (format === 'json' && asset?.lastResultId) {
            try {
                const url = await getAssetRawDataUrl(assetId, asset.lastResultId);
                if (url) window.open(url, '_blank', 'noopener');
            } catch (err) {
                setScanToast({ kind: 'error', msg: 'Export failed: ' + err.message });
            }
            return;
        }
        setScanToast({ kind: 'info', msg: `${format.toUpperCase()} export coming soon` });
    }, [assetId, asset]);

    // Open a tab by (group, sub) and scroll into view
    const openTab = useCallback((group, sub) => {
        setActiveGroup(group);
        setActiveSub(sub);
        setTimeout(() => {
            const el = document.getElementById('v3-tab-panel');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
    }, []);

    // Metric card click handlers scroll to Discovery/Findings sub-tabs
    const handlers = useMemo(() => ({
        onVuln:       () => openTab('findings',  'vulnerabilities'),
        onPorts:      () => openTab('discovery', 'ports'),
        onSubdomains: () => openTab('discovery', 'subdomains'),
        onTech:       () => openTab('discovery', 'technologies'),
        onDrift:      () => openTab('history',   'drift')
    }), [openTab]);

    const handleDriftEventClick = useCallback((ev) => {
        // Deep-link to Drift Alarms drawer if alarm has an alarmId
        if (ev && ev.alarmId) {
            navigate(`/drift-alarms?alarmId=${ev.alarmId}`);
        } else {
            navigate('/drift-alarms');
        }
    }, [navigate]);

    // Drop the existing activeSub if switching to a group that doesn't have it
    const handleGroupChange = useCallback((groupId) => {
        const group = TAB_GROUPS.find((g) => g.id === groupId);
        if (!group) return;
        setActiveGroup(groupId);
        const visibleTabs = group.tabs.filter((t) => {
            if (t.conditional === 'deepSec' && !asset?.lastDeepSecurityAt) return false;
            if (t.domainOnly && (asset?.assetType === 'ip' || asset?.assetType === 'cidr')) return false;
            return true;
        });
        if (!visibleTabs.find((t) => t.id === activeSub)) {
            setActiveSub(visibleTabs[0]?.id || '');
        }
    }, [asset, activeSub]);

    // PRD-51: Claude's score is primary, scan penalty is secondary
    const aiScore = asset?.baselineRiskScore ?? enrichment?.baselineRiskScore ?? null;
    const aiSev = riskScoreSeverity(aiScore);
    const scanPenalty = asset?.scanPenalty ?? null;
    const vulnCounts = {
        critical: asset?.criticalCount ?? 0,
        high: asset?.highCount ?? 0,
        medium: asset?.mediumCount ?? 0,
        low: asset?.lowCount ?? 0
    };
    const totalVulns = vulnCounts.critical + vulnCounts.high + vulnCounts.medium + vulnCounts.low;
    const vulnBand = vulnDominantBand(vulnCounts);
    const vulnHint = `${vulnCounts.critical}C ${vulnCounts.high}H ${vulnCounts.medium}M ${vulnCounts.low}L`;

    const ai = enrichment?.aiAnalysis || {};
    // PRD-45.5: Confidence score for metrics card secondary line
    const confidenceScore = ai.confidenceScore || null;
    const confidenceLabel = confidenceScore != null
        ? (confidenceScore >= 80 ? 'High' : confidenceScore >= 50 ? 'Medium' : 'Low')
        : null;

    // PRD-133 L4: surface breakdown-fetch failures instead of swallowing them (the silent
    // .catch(console.warn) is what hid the L0 404 from every owner for weeks). Shared by the
    // toggle and the retry affordance.
    const loadBreakdown = () => {
        setBreakdownError(null);
        getAssetScoreBreakdown(assetId)
            .then(data => setScoreBreakdown(data))
            .catch(err => setBreakdownError(err?.message || 'Could not load breakdown.'));
    };

    // PRD-133 WP5: click a risk-trend point → fetch the change-attribution explanation. L4 visible
    // error pattern applies here too (never a blank panel).
    const explainPoint = (point) => {
        const atTs = point?.date;
        if (!atTs) return;
        setAttribution({ loading: true, atTs, data: null, error: null });
        getAssetScoreExplain(assetId, atTs)
            .then(data => setAttribution({ loading: false, atTs, data, error: null }))
            .catch(err => setAttribution({ loading: false, atTs, data: null, error: err?.message || 'Could not load change details.' }));
    };
    const retryExplain = () => {
        if (attribution?.atTs) explainPoint({ date: attribution.atTs });
    };

    if (loading) {
        return (
            <CleanModernLayout activePage="assets">
                <div className="v3-wrapper"><div className="v3-loading">Loading asset…</div></div>
            </CleanModernLayout>
        );
    }

    if (error || !asset) {
        return (
            <CleanModernLayout activePage="assets">
                <div className="v3-wrapper">
                    <div className="v3-error">
                        <h2>Asset unavailable</h2>
                        <p>{error || 'Asset not found.'}</p>
                        <button type="button" className="v3-btn" onClick={() => navigate('/assets')}>Back to Assets</button>
                    </div>
                </div>
            </CleanModernLayout>
        );
    }

    // Render current group's tab content
    const activeGroupDef = TAB_GROUPS.find((g) => g.id === activeGroup);
    const visibleSubTabs = activeGroupDef?.tabs.filter((t) => {
        if (t.conditional === 'deepSec' && !asset.lastDeepSecurityAt) return false;
        if (t.domainOnly && (asset.assetType === 'ip' || asset.assetType === 'cidr')) return false;
        return true;
    }) || [];

    // Which sub-tab content should render
    const renderSubTabContent = () => {
        // Discovery
        if (activeGroup === 'discovery') {
            if (activeSub === 'technologies') {
                return (
                    <TechnologiesView
                        asset={asset}
                        assetId={assetId}
                        enrichment={enrichment}
                        suppressions={suppressions}
                        onSuppressionsChange={setSuppressions}
                    />
                );
            }
            const tabDef = activeGroupDef.tabs.find((t) => t.id === activeSub);
            if (tabDef?.section) {
                return (
                    <AssetDataSections
                        asset={asset}
                        assetId={assetId}
                        enrichment={enrichment}
                        suppressions={suppressions}
                        onSuppressionsChange={setSuppressions}
                        onAssetRiskChange={handleAssetRiskChange}
                        visibleSections={tabDef.section}
                        autoExpand={true}
                        hideHeaders={true}
                    />
                );
            }
        }
        // Findings
        if (activeGroup === 'findings') {
            if (activeSub === 'vulnerabilities') {
                // PRD-46: Use FindingsTab which reads from persistent assetfindings table,
                // not AssetDataSections which reads from scan blob (may be stale/incomplete).
                return <FindingsTab assetId={assetId} />;
            }
            if (activeSub === 'remediation') return <RemediationFullView recommendations={ai.prioritizedRecommendations} />;
            if (activeSub === 'hardening')   return <HardeningFullView defensive={ai.defensiveRecommendations} />;
        }
        // Intelligence
        if (activeGroup === 'intelligence') {
            if (activeSub === 'mitre')          return <MitreView mitre={ai.mitreAttackMapping} />;
            if (activeSub === 'pentest')        return <PentestView roadmap={ai.penetrationTestingRoadmap} />;
            if (activeSub === 'compliance')     return <ComplianceView compliance={ai.complianceMapping} />;
            if (activeSub === 'trend-analysis') return <TrendAnalysisView trendAnalysis={ai.trendAnalysis} />;
            if (activeSub === 'cloud-security') return <CloudSecurityView cloudFindings={ai.cloudSecurityFindings} />;
        }
        // History
        if (activeGroup === 'history') {
            if (activeSub === 'scan-history')  return <ScanHistoryView history={history} asset={asset} />;
            if (activeSub === 'trends')        return <TrendsView history={history} trendData={trendData} findingsTrendData={findingsTrendData} enrichment={enrichment} onPointClick={explainPoint} attribution={attribution} onRetryAttribution={retryExplain} />;
            if (activeSub === 'drift')         return <DriftView assetId={assetId} history={history} />;
            if (activeSub === 'scan-details')  return <ScanDetailsTab assetId={assetId} history={history} asset={asset} />;
            if (activeSub === 'raw-data')      return <RawDataView asset={asset} assetId={assetId} />;
            if (activeSub === 'deep-security' && asset.lastDeepSecurityAt)
                return <DeepSecurityTab asset={asset} assetId={assetId} />;
        }
        return <div className="v3-empty">Select a tab above to view its content.</div>;
    };

    return (
        <CleanModernLayout activePage="assets">
            <div className="v3-wrapper">
                {/* Header */}
                <header className="v3-header">
                    <div className="v3-header__identity">
                        <div className="v3-header__icon" aria-hidden="true">
                            {asset.assetType === 'ip' || asset.assetType === 'cidr' ? '▣' : '◉'}
                        </div>
                        <div className="v3-header__text">
                            <h1 className="v3-header__name">{asset.value}</h1>
                            <div className="v3-header__meta">
                                <span className="v3-type-badge">{(asset.assetType || 'asset').toUpperCase()}</span>
                                {asset.lastDiscoveryAt && <span className="v3-header__timestamp">Last discovered: {fmtDateShort(asset.lastDiscoveryAt)}</span>}
                                {asset.lastVulnScanAt && <span className="v3-header__timestamp">Last vuln scan: {fmtDateShort(asset.lastVulnScanAt)}</span>}
                            </div>
                        </div>
                    </div>
                    <QuickActions
                        assetId={assetId}
                        asset={asset}
                        onScan={handleScan}
                        scanInProgress={Boolean(scanningType)}
                    />
                </header>

                {scanToast && (
                    <div className={`v3-toast v3-toast--${scanToast.kind}`} role="status">
                        {scanToast.msg}
                        <button type="button" className="v3-toast__close" onClick={() => setScanToast(null)} aria-label="Dismiss">✕</button>
                    </div>
                )}

                {/* PRD-56: WAF challenge-blocking banner. Reads stability-layer
                    Display fields (miss-counter-stable) and falls back to raw
                    last-reading. Self-renders only when fraction > 0.5 AND
                    sample size >= 10; otherwise returns null. */}
                <WafBlockingBanner
                    assetId={assetId}
                    wafBlockingDisplayFraction={asset.wafBlockingDisplayFraction}
                    wafBlockingDisplayType={asset.wafBlockingDisplayType}
                    wafBlockingFraction={asset.wafBlockingFraction}
                    wafBlockingType={asset.wafBlockingType}
                    wafBlockingSampleSize={asset.wafBlockingSampleSize}
                />

                {/* PRD-65 Phase 3 + PRD-83 Phase C.3: coverage banners.
                    URL-cap variant self-renders only when the vuln-scan dropped >50 URLs
                    AND >=20% of total (Enterprise tierCap=null never shows). CMS variant
                    self-renders when a vuln-scan detected WP/Drupal/Joomla but produced
                    0 CMS-specific findings — distinguishes "scanned clean" from "no
                    coverage at all". Both can show simultaneously. */}
                <CoverageBanner
                    assetId={assetId}
                    urlsTotal={asset.lastVulnScanUrlsTotal ?? asset.lastDeepSecurityUrlsTotal}
                    urlsSelected={asset.lastVulnScanUrlsSelected ?? asset.lastDeepSecurityUrlsSelected}
                    urlsTruncated={asset.lastVulnScanUrlsTruncated ?? asset.lastDeepSecurityUrlsTruncated}
                    tierCapApplied={asset.lastVulnScanTierCap ?? asset.lastDeepSecurityTierCap}
                    currentTier={currentTier}
                    cmsDetected={asset.lastVulnScanCmsDetected}
                    cmsFindings={asset.lastVulnScanCmsFindings}
                    cmsTemplatesRun={asset.lastVulnScanCmsTemplatesRun}
                    assetType={asset.assetType}
                    lastPortScanTimeout={asset.lastPortScanTimeout}
                />

                {/* Compact 6-card metrics row */}
                <div className="v3-metrics">
                    <MetricsCard
                        compact
                        label="Risk Score"
                        value={aiScore != null ? aiScore : '—'}
                        sublabel={aiSev.label}
                        severity={aiSev.band}
                        secondary={
                            scanPenalty != null
                                ? `Scan Penalty: ${scanPenalty}${confidenceLabel ? ` · ${confidenceLabel} conf.` : ''}`
                                : null
                        }
                    />
                    <MetricsCard
                        compact
                        label="Vulnerabilities"
                        value={totalVulns}
                        hint={vulnHint}
                        severity={vulnBand}
                        onClick={handlers.onVuln}
                    />
                    <MetricsCard
                        compact
                        label="Open Ports"
                        value={asset.openPortCount != null ? asset.openPortCount : '—'}
                        severity="neutral"
                        onClick={handlers.onPorts}
                    />
                    <MetricsCard
                        compact
                        label="Subdomains"
                        value={asset.subdomainCount != null ? asset.subdomainCount : '—'}
                        hint={asset.liveSubdomainCount != null && asset.subdomainCount != null ? `${asset.liveSubdomainCount} live` : undefined}
                        severity="neutral"
                        onClick={handlers.onSubdomains}
                    />
                    <MetricsCard
                        compact
                        label="Technologies"
                        value={asset.technologyCount != null ? asset.technologyCount : '—'}
                        severity="neutral"
                        onClick={handlers.onTech}
                    />
                    <MetricsCard
                        compact
                        label="Drift"
                        value={driftOpenCount > 0 ? 'ALERT' : 'Stable'}
                        hint={driftOpenCount > 0 ? `${driftOpenCount} open` : 'No open events'}
                        severity={driftOpenCount > 0 ? 'high' : 'low'}
                        onClick={handlers.onDrift}
                    />
                </div>

                {/* PRD-51: Score Breakdown — expandable */}
                <div className="v3-score-breakdown-toggle" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* PRD-133 WP6: ⓘ discoverability on the risk score — explainer + "see full
                        breakdown" affordance that opens the panel (triggering the breakdown fetch). */}
                    <Tooltip
                        category="context"
                        position="right"
                        content="Higher = riskier. Assessed by Claude from your active findings & exposure. Open the breakdown to see what's driving it."
                    >
                        <button
                            type="button"
                            aria-label="About the risk score — open the score breakdown"
                            onClick={() => {
                                if (!showBreakdown) {
                                    setShowBreakdown(true);
                                    if (!scoreBreakdown) loadBreakdown();
                                }
                            }}
                            style={{ fontSize: '0.95rem', lineHeight: 1, color: 'var(--cc-accent)', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
                        >
                            ⓘ
                        </button>
                    </Tooltip>
                    <button
                        className="v3-link-btn"
                        onClick={() => {
                            setShowBreakdown(prev => !prev);
                            if (!scoreBreakdown && !showBreakdown) {
                                loadBreakdown();
                            }
                        }}
                        style={{ fontSize: '0.82rem', color: 'var(--cc-accent)', cursor: 'pointer', background: 'none', border: 'none', padding: '4px 0' }}
                    >
                        {showBreakdown ? '▾ Hide Score Breakdown' : '▸ Show Score Breakdown'}
                    </button>
                </div>
                {showBreakdown && breakdownError && !scoreBreakdown && (
                    <div
                        className="v3-score-breakdown"
                        role="status"
                        style={{ background: 'var(--cc-surface)', border: '1px solid var(--cc-border)', borderRadius: 8, padding: 16, marginBottom: 16, fontSize: '0.85rem' }}
                    >
                        <span style={{ opacity: 0.85 }}>Couldn't load the score breakdown.</span>
                        <button
                            className="v3-link-btn"
                            onClick={loadBreakdown}
                            style={{ marginLeft: 10, color: 'var(--cc-accent)', cursor: 'pointer', background: 'none', border: 'none', padding: 0, fontSize: '0.82rem' }}
                        >
                            Retry
                        </button>
                    </div>
                )}
                {showBreakdown && scoreBreakdown && (
                    <div className="v3-score-breakdown" style={{ background: 'var(--cc-surface)', border: '1px solid var(--cc-border)', borderRadius: 8, padding: 16, marginBottom: 16, fontSize: '0.85rem' }}>
                        {/* L3: the numeric riskLevel (server-side getRiskLevel(baselineRiskScore)) is
                            authoritative for this badge; the claudeReasoning narrative below renders
                            as italic commentary only, never as a contradicting level word. */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                            <div>
                                <strong>Risk Score: {scoreBreakdown.riskScore ?? '—'}</strong>
                                <span style={{ marginLeft: 8, opacity: 0.7 }}>({scoreBreakdown.riskLevel?.toUpperCase() || '—'})</span>
                                <span style={{ marginLeft: 12, opacity: 0.6 }}>Assessed by Claude</span>
                            </div>
                            {scoreBreakdown.confidence != null && (
                                <span style={{ opacity: 0.7 }}>Confidence: {scoreBreakdown.confidence}%</span>
                            )}
                        </div>
                        <div style={{ marginBottom: 8 }}>
                            <strong>Scan Penalty:</strong> {scoreBreakdown.scanPenalty ?? '—'} (uncapped)
                            {scoreBreakdown.scanPenaltyLevel && <span style={{ opacity: 0.7 }}> — {scoreBreakdown.scanPenaltyLevel}</span>}
                        </div>
                        {scoreBreakdown.findingsBySource && Object.keys(scoreBreakdown.findingsBySource).length > 0 && (
                            <div style={{ marginBottom: 8 }}>
                                <strong>What's driving the score:</strong>
                                <ScorePenaltyBar findingsBySource={scoreBreakdown.findingsBySource} />
                            </div>
                        )}
                        {scoreBreakdown.suppressedCount > 0 && (
                            <div style={{ marginBottom: 8, opacity: 0.8 }}>
                                Suppressed: {scoreBreakdown.suppressedCount} findings ignored (−{scoreBreakdown.suppressedPenaltyReduction} penalty)
                            </div>
                        )}
                        {/* Guard: claudeReasoning must be a string. PRD-59 schema-split can
                            make the upstream executiveSummary an object {headline, narrative};
                            rendering an object as a React child throws and blanks the panel. */}
                        {typeof scoreBreakdown.claudeReasoning === 'string' && scoreBreakdown.claudeReasoning && (
                            <div style={{ marginTop: 8, fontStyle: 'italic', opacity: 0.8, borderTop: '1px solid var(--cc-border)', paddingTop: 8 }}>
                                {scoreBreakdown.claudeReasoning}
                            </div>
                        )}
                    </div>
                )}

                {/* PRD-59 Tier 3 Fix E: Surface latent recompute failures so
                    customers don't trust a silently-stale risk score. */}
                {asset?.riskRecomputeError && (
                    <div
                        className="v3-toast v3-toast--error"
                        role="status"
                        style={{ margin: '0.5rem 0' }}
                    >
                        <span>
                            <strong>Risk score may be stale</strong>
                            {' '}— last successful recompute at{' '}
                            {asset.riskRecomputeErrorAt
                                ? new Date(asset.riskRecomputeErrorAt).toLocaleString()
                                : 'unknown'}
                            . We&apos;ll retry on the next scan.
                        </span>
                    </div>
                )}

                {/* Tighter visualization row (half height, 3 drift events by severity)
                    PRD-68 §3.5: `id="what-changed"` is the click-through anchor from the
                    Command Center AssetsWidget — this row (risk trend + recent drift
                    events) is the closest representation of "what changed since the
                    last scan" on this page. */}
                <div id="what-changed" className="v3-viz-row">
                    <div
                        className="v3-viz-card"
                        aria-label={
                            trendData.length > 0
                                ? `Risk score over time, currently ${trendData[trendData.length - 1].score}, ${
                                      trendData.filter((r) => new Date(r.date).getTime() > Date.now() - 30 * 24 * 60 * 60 * 1000).length
                                  } changes in last 30 days`
                                : 'Risk score over time, no data yet'
                        }
                    >
                        <div className="v3-viz-card__header">
                            <h3>Risk Score Trend</h3>
                            <span className="v3-viz-card__subtitle">{trendData.length} point{trendData.length === 1 ? '' : 's'}</span>
                        </div>
                        <RiskTrendChart
                            data={trendData}
                            emptyMessage="No risk timeline yet — score will populate after the first scan."
                            onPointClick={explainPoint}
                        />
                        {attribution && (
                            <ScoreAttributionPanel
                                attribution={attribution.data}
                                loading={attribution.loading}
                                error={attribution.error}
                                onRetry={retryExplain}
                            />
                        )}
                    </div>
                    <div className="v3-viz-card">
                        <DriftEventsStrip
                            assetId={assetId}
                            limit={3}
                            sortBy="severity"
                            onEventClick={handleDriftEventClick}
                        />
                    </div>
                </div>

                {/* Attack Surface */}
                {ai.attackSurface && (
                    <CollapsibleSection
                        title="Attack Surface Analysis"
                        subtitle="Exposed services, critical technologies, misconfigurations, information leakage"
                        defaultOpen={true}
                    >
                        <AttackSurfaceBlock attackSurface={ai.attackSurface} />
                    </CollapsibleSection>
                )}

                {/* Executive Summary */}
                {ai.executiveSummary && (
                    <CollapsibleSection
                        title="Executive Summary"
                        subtitle="AI-generated narrative of the asset's security posture"
                        defaultOpen={true}
                    >
                        {(() => {
                            const summary = ai.executiveSummary;
                            // Legacy shape: plain string
                            if (typeof summary === 'string') {
                                return <p className="v3-intel-text">{summary}</p>;
                            }
                            // New schema-split shape: { headline, narrative }
                            const validationError = asset?.enrichmentValidationError;
                            return (
                                <>
                                    {summary.headline && (
                                        <p className="v3-intel-text v3-intel-text--lede" style={{ fontWeight: 600 }}>
                                            {summary.headline}
                                        </p>
                                    )}
                                    {summary.narrative ? (
                                        <p className="v3-intel-text">{summary.narrative}</p>
                                    ) : (
                                        <div>
                                            <p className="v3-intel-text">
                                                AI narrative temporarily unavailable. See the Findings tab for current details.
                                            </p>
                                            {asset?.lastEnrichmentAt && (
                                                <p className="v3-intel-text" style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                                    Last successful analysis: {new Date(asset.lastEnrichmentAt).toLocaleDateString()}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    {validationError === 'narrative-fabrication' && (
                                        <span
                                            className="cc-badge cc-badge-medium"
                                            title="Last AI narrative failed validation and was withheld. The headline is mechanically generated from cumulative state."
                                        >
                                            AI narrative withheld
                                        </span>
                                    )}
                                </>
                            );
                        })()}
                    </CollapsibleSection>
                )}

                {/* Side-by-side abbreviated guidance cards */}
                <GuidanceCards
                    recommendations={ai.prioritizedRecommendations}
                    defensive={ai.defensiveRecommendations}
                    onOpenRemediation={() => openTab('findings', 'remediation')}
                    onOpenHardening={() => openTab('findings', 'hardening')}
                />

                {/* 2-level tab system */}
                <div className="v3-tab-system" id="v3-tab-panel">
                    <div className="v3-group-tabs" role="tablist" aria-label="Asset details sections">
                        {TAB_GROUPS.map((g) => (
                            <button
                                key={g.id}
                                type="button"
                                role="tab"
                                aria-selected={activeGroup === g.id}
                                className={`v3-group-tab ${activeGroup === g.id ? 'is-active' : ''}`}
                                onClick={() => handleGroupChange(g.id)}
                            >
                                <span className="v3-group-tab__label">{g.label}</span>
                                <span className="v3-group-tab__subtitle">{g.subtitle}</span>
                            </button>
                        ))}
                    </div>

                    <div className="v3-sub-tabs" role="tablist" aria-label={`${activeGroupDef?.label} views`}>
                        {visibleSubTabs.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                role="tab"
                                aria-selected={activeSub === t.id}
                                className={`v3-sub-tab ${activeSub === t.id ? 'is-active' : ''}`}
                                onClick={() => setActiveSub(t.id)}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    <div className="v3-sub-tab-panel" role="tabpanel">
                        {renderSubTabContent()}
                    </div>
                </div>
            </div>
        </CleanModernLayout>
    );
}

function AttackSurfaceBlock({ attackSurface }) {
    if (!attackSurface) return null;
    const {
        exposedServices = [],
        entryPoints = [],
        criticalAssets = [],
        criticalTechnologies = [],
        misconfigurations = [],
        informationLeakage = [],
        summary
    } = attackSurface;
    return (
        <div>
            {summary && <p className="v3-intel-text v3-intel-text--lede">{summary}</p>}
            <div className="v3-attack-grid">
                {[
                    { title: 'Exposed Services', items: exposedServices },
                    { title: 'Entry Points', items: entryPoints },
                    { title: 'Critical Assets', items: criticalAssets },
                    { title: 'Critical Technologies', items: criticalTechnologies },
                    { title: 'Misconfigurations', items: misconfigurations },
                    { title: 'Information Leakage', items: informationLeakage }
                ].map(({ title, items }) => items.length > 0 && (
                    <div key={title} className="v3-attack-card">
                        <h4>{title}</h4>
                        <ul>
                            {items.map((it, i) => (
                                <li key={i}>
                                    {typeof it === 'string'
                                        ? it
                                        : (it.name || it.service || it.technology || it.description || it.issue || JSON.stringify(it))}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default AssetDetails;
